import React from "react";
import { Button, Tooltip } from "antd";
import { CloseCircleFilled, SwapOutlined } from "@ant-design/icons";
import MaterialIcon from "components/svg-icons/material-icon";
import { DateUtils } from "utils/dateutils";
import ActivityIcon from "components/svg-icons/activity-icon";

type ErrorDetail = {
  number: number | null;
  reverse_dependency: boolean;
  spec_sections: { spec_no: string; spec_name: string }[];
  title: string;
  start_date?: string;
  end_date?: string;
  linked_to_end_date?: boolean;
  feature_type_id: number;
};

type ErrorItem = {
  message: string;
  extensions?: {
    code?: string;
    details?: ErrorDetail[];
  };
};

export type ErrorResponse = {
  error?: ErrorItem;
  errors?: ErrorItem[];
};

export interface ErrorPopUpProps {
  data: ErrorResponse;
  open: boolean;
  onProceed: () => void;
  onClose?: () => void;
}

function getRows(data: ErrorResponse): ErrorDetail[] {
  const singleErrorDetails = data.error?.extensions?.details;
  if (Array.isArray(singleErrorDetails) && singleErrorDetails.length) {
    return singleErrorDetails;
  }

  const errors = data.errors || [];
  if (errors.length) {
    return errors.flatMap((err) =>
      Array.isArray(err.extensions?.details) && err.extensions?.details?.length
        ? err.extensions.details
        : [
            {
              number: null,
              reverse_dependency: false,
              spec_sections: [],
              title: err.message,
              feature_type_id: 0
            }
          ]
    );
  }
  return [];
}

export function ErrorPopUp({
  data,
  open,
  onProceed,
  onClose
}: ErrorPopUpProps) {
  if (!open) return null;

  const rows = getRows(data);
  const headerMessage =
    data.error?.message ||
    data.errors?.[0]?.message ||
    "Conflicting Links Detected";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && onClose) onClose();
          if (e.key === "Escape" && onClose) onClose();
        }}
        aria-label="Close"
        data-testid="error-popup-close"
      />
      <div
        className="relative z-10 w-[720px] max-w-[90vw] rounded-lg bg-white p-6 shadow-xl"
        data-testid="error-popup-container"
      >
        <div className="mb-5 flex items-start gap-3">
          <CloseCircleFilled className="text-red-500 pt-1 hover:text-red-500 pt-[6.4px]" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Conflicting Links Detected
            </h2>
            <p className="mt-1 text-sm text-gray-600">{headerMessage}</p>
          </div>
        </div>

        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div
              key={`${row.title}-${row.number ?? idx}`}
              className="flex flex-col w-full features-card"
              data-testid="error-popup-row"
            >
              <div className="border border-color px-1 py-2 bg-white rounded flex items-center gap-2 mb-2">
                <div className="shrink-0 pt-[5px]">
                  {row.feature_type_id !== 9 ? (
                    <MaterialIcon
                      className="mr grow-none"
                      size={20}
                      fill="#a8a8a8"
                    />
                  ) : (
                    <ActivityIcon size={20} fill="#a8a8a8" />
                  )}
                </div>
                <div className="shrink-0">
                  {row.feature_type_id !== 9 ? (
                    <i className="entity-id-with-spec">
                      {row.spec_sections?.[0]
                        ? `${row.spec_sections[0]?.spec_no} - ${row.spec_sections[0]?.spec_name}`
                        : ""}
                    </i>
                  ) : (
                    <i className="entity-id-with-spec">
                      {row.number ? `${row.number}` : ""}
                    </i>
                  )}
                </div>
                <div className="min-w-0 truncate text-sm font-medium text-gray-900 max-w-[150px]">
                  <Tooltip
                    className="flex-grow truncate"
                    placement="topLeft"
                    title={row.title}
                  >
                    {row.title}
                  </Tooltip>
                </div>
                {row?.linked_to_end_date ? (
                  <div className="shrink-0 text-sm text-gray-500">
                    <i className="entity-id-with-spec">
                      End Date:{" "}
                      {row?.end_date ? DateUtils.format(row?.end_date) : ""}
                    </i>
                  </div>
                ) : (
                  row?.start_date && (
                    <div className="shrink-0 text-sm text-gray-500">
                      <i className="entity-id-with-spec">
                        Start Date:{" "}
                        {row?.start_date
                          ? DateUtils.format(row?.start_date)
                          : /* enclosing `row?.start_date &&` guard already proved start_date truthy */
                            /* v8 ignore next */
                            ""}
                      </i>
                    </div>
                  )
                )}
                {row.feature_type_id === 9 && row.reverse_dependency && (
                  <div className="shrink-0 ml-auto">
                    <div
                      className="flex items-center bg-ciq-gray-100 rounded h-6 space-x-1 px-2 w-[186px]"
                      data-testid="fal-reverse-dependency"
                    >
                      <span className="flex items-center">
                        <SwapOutlined
                          className="text-ciq-gray-700"
                          data-testid="fal-swap-icon"
                        />
                      </span>
                      <span className="flex items-center font-normal text-xs">
                        Activity is the Predecessor
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <Button
            type="primary"
            onClick={onProceed}
            data-testid="error-popup-proceed"
          >
            Proceed
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ErrorPopUp;
